/**
 * Audio notification utility for playing sounds on events
 */

import { STORAGE_KEYS } from '../constants/storage';

class AudioNotificationManager {
  private audioContext: AudioContext | null = null;
  private enabled: boolean = true;

  constructor() {
    // Initialize Web Audio API
    this.initializeAudio();

    // Load user preference
    const savedPreference = localStorage.getItem(STORAGE_KEYS.AUDIO_NOTIFICATIONS_ENABLED);
    if (savedPreference !== null) {
      this.enabled = savedPreference === 'true';
    }

    console.log('[AudioNotification] Initialized. Enabled:', this.enabled);
  }

  private initializeAudio() {
    try {
      // Initialize AudioContext for generating tones
      if (typeof AudioContext !== 'undefined') {
        this.audioContext = new AudioContext();
      } else if (
        typeof (window as unknown as Record<string, unknown>).webkitAudioContext !== 'undefined'
      ) {
        type AudioContextConstructor = new () => AudioContext;
        const WebkitAudioContext = (window as unknown as Record<string, unknown>)
          .webkitAudioContext as AudioContextConstructor;
        this.audioContext = new WebkitAudioContext();
      }

      if (!this.audioContext) {
        console.error('[AudioNotification] Web Audio API not available');
        this.enabled = false;
      } else {
        console.log('[AudioNotification] Audio initialized successfully');
      }
    } catch (error) {
      console.error('[AudioNotification] Failed to initialize audio:', error);
      this.enabled = false;
    }
  }

  private async playTone(frequency: number, duration: number, volume: number = 0.5): Promise<void> {
    if (!this.audioContext || !this.enabled) return;

    try {
      // Resume context if suspended (required for user interaction)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      // Set initial volume
      gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);

      // Fade out to avoid clicking
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration);

      console.log('[AudioNotification] Playing tone:', frequency, 'Hz for', duration * 1000, 'ms');
    } catch (error) {
      console.error('[AudioNotification] Failed to play tone:', error);
    }
  }

  /**
   * Play success notification sound
   */
  public async playSuccess(): Promise<void> {
    console.log('[AudioNotification] playSuccess called. Enabled:', this.enabled);

    if (!this.enabled || !this.audioContext) {
      console.log(
        '[AudioNotification] Success sound skipped. Enabled:',
        this.enabled,
        'Context:',
        !!this.audioContext
      );
      return;
    }

    // Play a pleasant higher-pitched beep for success
    await this.playTone(800, 0.2, 0.3);
  }

  /**
   * Play error notification sound
   */
  public async playError(): Promise<void> {
    console.log('[AudioNotification] playError called. Enabled:', this.enabled);

    if (!this.enabled || !this.audioContext) {
      console.log(
        '[AudioNotification] Error sound skipped. Enabled:',
        this.enabled,
        'Context:',
        !!this.audioContext
      );
      return;
    }

    // Play a lower-pitched beep for errors
    await this.playTone(400, 0.3, 0.3);
  }

  /**
   * Enable or disable audio notifications
   */
  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    localStorage.setItem(STORAGE_KEYS.AUDIO_NOTIFICATIONS_ENABLED, String(enabled));
    console.log('[AudioNotification] Audio notifications', enabled ? 'enabled' : 'disabled');
  }

  /**
   * Check if audio notifications are enabled
   */
  public isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Test audio playback by playing both sounds
   */
  public async testSounds(): Promise<void> {
    console.log('[AudioNotification] Testing sounds...');
    await this.playSuccess();
    setTimeout(async () => await this.playError(), 1000);
  }
}

// Export singleton instance
export const audioNotification = new AudioNotificationManager();
